import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { TuiAlertService } from '@taiga-ui/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { finalize, Subject, takeUntil } from 'rxjs';

import {
  DriverRow,
  GanttData,
  GetGanttModalDetails,
  ServiceUbication,
} from 'src/app/models/gantt/gantt.model';
import { NodeStatusUpdateRequest } from 'src/app/models/service-manager/node-status.model';
import { GanttService } from 'src/app/services/gantt/gantt.service';
import { HistoryServicesService } from 'src/app/services/history-services/history-services.service';
import { ServicesService } from 'src/app/services/service/services.service';
import { EMPTY_GUID, formatSecondsToHourAndMinutes } from 'src/app/utils';

declare const MarkerClusterer: any; // [ER] - No he tenido otra opción ya que los de los de ceras ideas en vez de utlizar la libreria cargado la API a traves del index.html y esto es algo pendiente a cambiar

@Component({
  selector: 'app-gantt-weekly-modal',
  templateUrl: './gantt-weekly-modal.component.html',
  styleUrls: ['./gantt-weekly-modal.component.css'],
})
export class GanttWeeklyModalComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  /* ------------ inputs / refs ------------ */
  @Input() taskGanttData!: GanttData;
  @Input() driverRow?: DriverRow;
  @Output() updateGantt = new EventEmitter<boolean>();

  @ViewChild('littleGmapContainer')
  gmap?: ElementRef<HTMLDivElement>;

  /* ------------ state ------------ */
  taskGanttDataDetails!: GetGanttModalDetails;
  isNodeLockedForDriverAssignation = false;

  private viewReady: boolean = false;
  private detailsReady: boolean = false;

  loading: boolean = true;
  noData: boolean = false;
  nodeStatusLoading: boolean = false;

  private destroy$ = new Subject<void>();

  /* ------------ Google Maps handles ------------ */
  private map!: google.maps.Map;
  private directionsRenderer!: google.maps.DirectionsRenderer;
  private markers: google.maps.Marker[] = [];
  private markerCluster!: any; // [ER] - No he tenido otra opción ya que los de los de ceras ideas en vez de utlizar la libreria cargado la API a traves del index.html y esto es algo pendiente a cambiar

  /* -------------------------------------------- */
  constructor(
    public bsModalRef: BsModalRef,
    private ganttService: GanttService,
    private servicesSvc: ServicesService,
    private alertService: TuiAlertService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private historyService: HistoryServicesService,
    private router: Router
  ) {}

  /* ===== lifecycle ================================================= */

  ngOnInit(): void {
    this.loadModalData();
  }

  get hasDriverAssigned(): boolean {
    return !!this.driverRow && this.driverRow.guid !== EMPTY_GUID;
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.maybeInitMap();
  }

  private maybeInitMap(): void {
    if (!this.viewReady || !this.detailsReady || !this.gmap) {
      return;
    }

    this.initMap();
    this.drawRoute();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.directionsRenderer.setMap(null);
    this.markers.forEach((m) => m.setMap(null));
    this.markerCluster?.clearMarkers();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ===== data ====================================================== */

  private loadModalData(): void {
    this.loading = true;
    this.noData = false;

    this.ganttService
      .getGanttModalData(this.taskGanttData.nodeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (details) => {
          this.taskGanttDataDetails = details;
          this.loading = false;
          this.detailsReady = true;
          this.isNodeLockedForDriverAssignation =
            details.isNodeLockedForDriverAssignation;

          this.cdr.detectChanges();
          this.maybeInitMap();
        },
        error: (err) => {
          this.loading = false;
          this.noData = true;

          const message =
            err?.error?.message ??
            this.translate.instant('service_manager.modal.error_loading_modal');
          this.alertService
            .open(message, {
              label: this.translate.instant('service_manager.modal.errorLabel'),
              status: 'error',
            })
            .subscribe();
        },
      });
  }

  onToggle(locked: boolean) {
    this.isNodeLockedForDriverAssignation = locked;
    setTimeout(() => this.updateNodeStatus(), 0);
  }

  /* ===== map ======================================================= */

  private initMap(): void {
    const { latitude, longitude } = this.taskGanttDataDetails.startUbication;
    this.map = new google.maps.Map(this.gmap!.nativeElement, {
      center: { lat: latitude, lng: longitude },
      zoom: 6,
      disableDefaultUI: true,
      zoomControl: true,
    });

    this.directionsRenderer = new google.maps.DirectionsRenderer({
      map: this.map,
      suppressMarkers: true,
    });
  }

  private clearMarkers(): void {
    this.markers.forEach((m) => m.setMap(null));
    this.markers = [];
    this.markerCluster?.clearMarkers();
  }

  get isTransportNode(): boolean {
    return this.taskGanttData.nodeType === 'TRANSPORT_NODE';
  }

  get participantLabelKey(): string {
    switch (this.taskGanttData.nodeType) {
      case 'OPERATIVE_NODE':
        return 'service_manager.modal.client';
      case 'SWEEPER_NODE':
        return 'service_manager.modal.driver';
      default:
        return '';
    }
  }

  /** Texto a mostrar junto al icono */
  get participantName(): string {
    if (this.taskGanttData.nodeType === 'OPERATIVE_NODE') {
      return this.taskGanttDataDetails.clientName ?? '';
    }
    if (this.taskGanttData.nodeType === 'SWEEPER_NODE') {
      return this.driverRow?.displayName ?? '';
    }
    return '';
  }

  private createClusterAndFit(): void {
    // 1) Limpia marcadores/cluster previos
    this.markers.forEach((m) => m.setMap(null));
    this.markerCluster?.clearMarkers();

    // 2) Crea el clusterer y guarda la instancia
    this.markerCluster = new MarkerClusterer(this.map, this.markers, {
      imagePath:
        'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
      zoomOnClick: false,
    });

    // 3) Ajusta zoom para que entren todos los marcadores
    const bounds = new google.maps.LatLngBounds();
    this.markers.forEach((m) => {
      const pos = m.getPosition();
      if (pos) bounds.extend(pos);
    });
    if (!bounds.isEmpty()) this.map.fitBounds(bounds);

    // 4) Registra _una sola vez_ el listener de clusterclick
    google.maps.event.clearListeners(this.markerCluster, 'clusterclick');
    google.maps.event.addListener(
      this.markerCluster,
      'clusterclick',
      (cluster: any) => {
        this.showClusterInfo(cluster);
      }
    );
  }

  private async drawRoute(): Promise<void> {
    if (!this.map || !this.taskGanttDataDetails) return;

    const { startUbication: start, endUbication: end } =
      this.taskGanttDataDetails;

    if (start.latitude === end.latitude && start.longitude === end.longitude) {
      this.clearMarkers();
      this.pushMarker(
        this.latLng(start),
        'assets/img/icon_service.png',
        this.taskGanttDataDetails.startUbication.address
      );

      // 2º marcador: entrega
      this.pushMarker(
        this.latLng(end),
        'assets/img/icon_service2.png',
        this.taskGanttDataDetails.endUbication.address
      );

      this.createClusterAndFit();
      return;
    }

    const ds = new google.maps.DirectionsService();

    const waypoints: google.maps.DirectionsWaypoint[] = (
      this.taskGanttDataDetails.providersUbication ?? []
    ).map((p) => ({
      location: this.toLatLngWayPoint({ lat: p.latitude, lng: p.longitude }),
    }));

    const request: google.maps.DirectionsRequest = {
      origin: this.latLng(this.taskGanttDataDetails.startUbication),
      destination: this.latLng(this.taskGanttDataDetails.endUbication),
      waypoints: waypoints,
      optimizeWaypoints: true,
      travelMode: google.maps.TravelMode.DRIVING,
    };

    const result = await new Promise<google.maps.DirectionsResult>(
      (ok, fail) => {
        ds.route(request, (res, status) =>
          status === google.maps.DirectionsStatus.OK ? ok(res) : fail(status)
        );
      }
    );

    this.directionsRenderer.setDirections(result);
    this.addMarkers(result);
    this.clusterMarkersListener();
  }

  private toLatLngWayPoint(pos: {
    lat: number;
    lng: number;
  }): google.maps.LatLng {
    return new google.maps.LatLng(pos.lat, pos.lng);
  }

  /* ===== markers & cluster ======================================== */

  private addMarkers(res: google.maps.DirectionsResult): void {
    const legs = res.routes[0].legs;
    legs.forEach((leg, i) => {
      // 1) marcador de inicio
      if (i === 0) {
        this.pushMarker(
          leg.start_location,
          'assets/img/icon_service.png',
          this.taskGanttDataDetails.startUbication.address
        );
      }
      // 2) waypoints intermedios
      leg.via_waypoints.forEach((wp, idx) => {
        const provider = this.taskGanttDataDetails.providersUbication?.[idx];
        this.pushMarker(wp, 'assets/img/icon_service3.png', provider?.address);
      });
      if (i === legs.length - 1) {
        this.pushMarker(
          leg.end_location,
          'assets/img/icon_service2.png',
          this.taskGanttDataDetails.endUbication.address
        );
      }
    });
  }

  private pushMarker(
    pos: google.maps.LatLng | google.maps.LatLngLiteral,
    icon: string,
    title?: string
  ): google.maps.Marker {
    const marker = new google.maps.Marker({
      position: pos,
      map: this.map,
      title: title,
      icon: { url: icon, scaledSize: new google.maps.Size(30, 30) },
    });
    this.markers.push(marker);
    return marker; // ← ahora devolvemos el Marker
  }

  private clusterMarkersListener(): void {
    this.markerCluster = new MarkerClusterer(this.map, this.markers, {
      imagePath:
        'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
      zoomOnClick: false,
    });

    this.markerCluster.addListener('clusterclick', (cluster: any) => {
      this.showClusterInfo(cluster);
    });
  }

  private showClusterInfo(cluster: any): void {
    const markers: google.maps.Marker[] =
      typeof cluster.getMarkers === 'function' ? cluster.getMarkers() : [];

    const rows = markers
      .map((m) => {
        const iconVal = m.getIcon();
        let url = '';
        if (typeof iconVal === 'string') {
          url = iconVal;
        } else if (
          iconVal &&
          typeof iconVal === 'object' &&
          'url' in iconVal &&
          typeof (iconVal as any).url === 'string'
        ) {
          url = (iconVal as any).url;
        }
        const title = m.getTitle() ?? '';
        return `
          <tr style="vertical-align: middle; height: 36px;">
            <td style="padding: 0 8px;"><img src="${url}" width="24" /></td>
            <td style="padding: 0 8px; white-space: nowrap;">${title}</td>
          </tr>
        `;
      })
      .join('');

    const content = `
      <div style="
        max-width: 240px;
        max-height: 160px;
        overflow-x: auto;
        overflow-y: auto;
        padding: 4px;
        box-sizing: border-box;
      ">
        <table style="
          border-collapse: separate;
          border-spacing: 0 4px;
          width: 100%;
        ">
          <tbody>
            ${rows}
          </tbody>
        </table>
      </div>
    `;

    new google.maps.InfoWindow({
      content,
      position:
        typeof cluster.getCenter === 'function'
          ? cluster.getCenter()
          : this.map.getCenter()!,
    }).open(this.map);
  }

  /* ===== helpers =================================================== */

  private latLng(u: ServiceUbication): google.maps.LatLngLiteral {
    return { lat: u.latitude, lng: u.longitude };
  }

  /* ===== UI actions =============================================== */

  closeModal(): void {
    this.bsModalRef?.hide();
  }

  getFormattedTime(seconds: string): string {
    return formatSecondsToHourAndMinutes(seconds);
  }

  updateNodeStatus(): void {
    if (
      !this.taskGanttDataDetails.orderHash ||
      !this.taskGanttDataDetails.nodeId
    ) {
      const msg = this.translate.instant(
        'service_manager.modal.node_locked_missing_data'
      );
      this.alertService
        .open(msg, { status: 'error', autoClose: true })
        .subscribe();
      return;
    }

    const payload: NodeStatusUpdateRequest = {
      orderHash: this.taskGanttDataDetails.orderHash,
      nodeId: this.taskGanttDataDetails.nodeId,
      desiredBlockState: this.isNodeLockedForDriverAssignation,
      isSweeperDriver: false
    };

    this.nodeStatusLoading = true;

    this.servicesSvc
      .updateNodeStatus(payload)
      .pipe(finalize(() => (this.nodeStatusLoading = false)))
      .subscribe({
        next: (response) => {
          if (response.wasSuccessful) {
            const key = this.isNodeLockedForDriverAssignation
              ? 'service_manager.modal.node_locked_success'
              : 'service_manager.modal.node_unlocked_success';

            this.alertService
              .open(this.translate.instant(key), {
                status: 'success',
                autoClose: true,
              })
              .subscribe();
            this.updateGantt.emit(true);
          } else {
            // Aquí ya no existe `err`, así que tiramos de mensaje genérico
            this.isNodeLockedForDriverAssignation = !payload.desiredBlockState;

            const errorKey = this.isNodeLockedForDriverAssignation
              ? 'service_manager.modal.node_unlocked_error'
              : 'service_manager.modal.node_locked_error';

            this.alertService
              .open(this.translate.instant(errorKey), {
                status: 'error',
                autoClose: true,
              })
              .subscribe();
          }
        },
        error: (err: any) => {
          this.isNodeLockedForDriverAssignation = !payload.desiredBlockState;

          const key = this.isNodeLockedForDriverAssignation
            ? 'service_manager.modal.node_unlocked_error'
            : 'service_manager.modal.node_locked_error';

          this.alertService
            .open(this.translate.instant(key, { error: err.message || err }), {
              status: 'error',
              autoClose: true,
            })
            .subscribe();
        },
      });
  }

  viewDetail(): void {

    this.historyService.setData({
      orderHash: this.taskGanttDataDetails.orderHash,
    });

    const urlTree = this.router.createUrlTree(
      ['/service-manager/detail-service'],
      {
        queryParams: { orderHash: this.taskGanttDataDetails.orderHash },
      }
    );

    const fullUrl = `${window.location.origin}/#/${urlTree.toString()}`;

    window.open(fullUrl, '_blank');
  }
}
